import React from 'react';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import AttributeService from '../../../services/AttributeService';
import NonconformityReasonService, {
  INonconformityReason,
  INonconformityReasonPayload,
} from '../../../services/NonconformityReasonService';
import ToolService from '../../../services/ToolService';
import { normalizeTurkish } from '../../../utils';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface InspectorAttribute {
  id?: number;
  key?: string;
  name?: string | null;
  description?: string | null;
  isActive?: boolean | null;
  active?: boolean | null;
  order?: number | null;
  toolId?: number | null;
  isGroup?: boolean;
  children?: InspectorAttribute[];
}

interface ToolItem {
  id: number;
  name?: string | null;
  description?: string | null;
}

const ensureStringValue = (value: unknown): string =>
  typeof value === 'string' ? value : value === null || value === undefined ? '' : String(value);

const toPositiveNumber = (value: unknown): number | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const isInspectorAttributeActive = (attribute: InspectorAttribute): boolean =>
  (attribute.isActive ?? attribute.active) === true;

const InspectorNonconformityReasonsPage: React.FC = () => {
  const [attributes, setAttributes] = React.useState<InspectorAttribute[]>([]);
  const [tools, setTools] = React.useState<ToolItem[]>([]);
  const [attributesLoading, setAttributesLoading] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState('');
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<React.Key[]>([]);

  const [selectedAttribute, setSelectedAttribute] = React.useState<InspectorAttribute | null>(null);
  const [reasonDrawerVisible, setReasonDrawerVisible] = React.useState(false);
  const [reasonItems, setReasonItems] = React.useState<INonconformityReason[]>([]);
  const [reasonLoading, setReasonLoading] = React.useState(false);
  const [reasonError, setReasonError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<'PRIVATE' | 'ALL'>('PRIVATE');
  const [reasonSearchText, setReasonSearchText] = React.useState('');

  const [createDrawerVisible, setCreateDrawerVisible] = React.useState(false);
  const [createSubmitting, setCreateSubmitting] = React.useState(false);
  const [editingReason, setEditingReason] = React.useState<INonconformityReason | null>(null);
  const [form] = Form.useForm();

  const currentAccountId = React.useMemo(() => toPositiveNumber(localStorage.getItem('id')), []);
  const currentAccountLabel = React.useMemo(() => {
    const firstName = ensureStringValue(localStorage.getItem('firstName')).trim();
    const lastName = ensureStringValue(localStorage.getItem('lastName')).trim();
    const email = ensureStringValue(localStorage.getItem('email')).trim();
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (fullName && email) {
      return `${fullName} (${email})`;
    }
    return fullName || email || (currentAccountId ? `Kullanıcı #${currentAccountId}` : 'Kullanıcı');
  }, [currentAccountId]);

  const fetchAttributes = React.useCallback(async () => {
    setAttributesLoading(true);
    try {
      const [attributeResponse, toolResponse] = await Promise.all([
        AttributeService.getInspectorAttributes({ size: 1000, sort: 'order,asc' }),
        ToolService.getAllTools(),
      ]);
      const content: InspectorAttribute[] = Array.isArray(attributeResponse?.data?.content)
        ? attributeResponse.data.content
        : Array.isArray(attributeResponse?.data)
          ? attributeResponse.data
          : [];
      const activeAttributes: InspectorAttribute[] = content
        .filter(isInspectorAttributeActive)
        .sort((a: InspectorAttribute, b: InspectorAttribute) => {
          if (a.order == null && b.order == null) {
            return 0;
          }
          if (a.order == null) {
            return 1;
          }
          if (b.order == null) {
            return -1;
          }
          return a.order - b.order;
        });
      const nextTools = Array.isArray(toolResponse?.data) ? toolResponse.data : [];
      setAttributes(activeAttributes);
      setTools(nextTools);
      const groupedToolIds = new Set(activeAttributes.map((item) => item.toolId).filter(Boolean));
      setExpandedRowKeys(Array.from(groupedToolIds).map((toolId) => `group-${toolId}`));
    } catch (error) {
      console.error('InspectorNonconformityReasonsPage: attributes failed', error);
      message.error('Parametreler yüklenemedi.');
      setAttributes([]);
      setTools([]);
      setExpandedRowKeys([]);
    } finally {
      setAttributesLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void fetchAttributes();
  }, [fetchAttributes]);

  const groupedData = React.useMemo(() => {
    const result: InspectorAttribute[] = [];
    attributes.forEach((item) => {
      if (!item.toolId) {
        result.push({ ...item, key: String(item.id), isGroup: false });
        return;
      }

      const tool = tools.find((toolItem) => toolItem.id === item.toolId);
      const toolName = tool?.name || 'Diğer';
      const toolDescription = tool?.description || 'Diğer';
      let group = result.find((groupItem) => groupItem.isGroup && groupItem.toolId === item.toolId);
      if (!group) {
        group = {
          key: `group-${item.toolId}`,
          name: toolName,
          description: toolDescription,
          isGroup: true,
          toolId: item.toolId,
          children: [],
        };
        result.push(group);
      }
      group.children?.push({ ...item, key: String(item.id), isGroup: false });
    });
    return result;
  }, [attributes, tools]);

  const treeData = React.useMemo(() => {
    const normalizedSearch = normalizeTurkish(searchInput.trim());
    return groupedData
      .map((item) => {
        if (item.isGroup) {
          const sortedChildren = [...(item.children || [])].sort((a, b) => {
            if (a.order == null && b.order == null) {
              return 0;
            }
            if (a.order == null) {
              return 1;
            }
            if (b.order == null) {
              return -1;
            }
            return a.order - b.order;
          });
          const groupMatched = [item.name, item.description]
            .some((field) => normalizeTurkish(String(field ?? '')).includes(normalizedSearch));
          const filteredChildren = normalizedSearch.length && !groupMatched
            ? sortedChildren.filter((child) =>
                [child.name, child.description]
                  .some((field) => normalizeTurkish(String(field ?? '')).includes(normalizedSearch)),
              )
            : sortedChildren;
          const groupOrder = filteredChildren.length
            ? Math.min(...filteredChildren.map((child) => child.order ?? 999999))
            : 999999;
          return filteredChildren.length ? { ...item, children: filteredChildren, order: groupOrder } : null;
        }
        const rowMatched = [item.name, item.description]
          .some((field) => normalizeTurkish(String(field ?? '')).includes(normalizedSearch));
        return !normalizedSearch.length || rowMatched ? item : null;
      })
      .filter(Boolean)
      .sort((a: any, b: any) => {
        if (a.order == null && b.order == null) {
          return 0;
        }
        if (a.order == null) {
          return 1;
        }
        if (b.order == null) {
          return -1;
        }
        return a.order - b.order;
      }) as InspectorAttribute[];
  }, [groupedData, searchInput]);

  const selectedAttributeId = selectedAttribute?.id;

  const privateReasons = React.useMemo(
    () =>
      currentAccountId
        ? reasonItems.filter((item) => {
            const ownerAccountId = item?.accountId ?? item?.account?.id;
            return ownerAccountId === currentAccountId && item?.isGeneral !== true;
          })
        : [],
    [currentAccountId, reasonItems],
  );

  const visibleReasons = React.useMemo(
    () => (activeTab === 'PRIVATE' ? privateReasons : reasonItems),
    [activeTab, privateReasons, reasonItems],
  );

  const searchedReasons = React.useMemo(() => {
    const normalizedSearch = normalizeTurkish(reasonSearchText.trim());
    if (!normalizedSearch.length) {
      return visibleReasons;
    }
    return visibleReasons.filter((reason) => {
      const ownerName = `${reason.account?.firstName ?? ''} ${reason.account?.lastName ?? ''}`.trim();
      const fields = [
        reason.abbreviation,
        reason.description,
        reason.attributeName,
        ownerName,
        reason.account?.email,
      ];
      return fields.some((field) => normalizeTurkish(String(field ?? '')).includes(normalizedSearch));
    });
  }, [reasonSearchText, visibleReasons]);

  const loadReasons = React.useCallback(async () => {
    if (!selectedAttributeId) {
      setReasonItems([]);
      setReasonError('Parametre bilgisi bulunamadı.');
      return;
    }
    setReasonLoading(true);
    setReasonError(null);
    try {
      const response = await NonconformityReasonService.listAll({
        'attributeId.equals': selectedAttributeId,
      });
      setReasonItems(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('InspectorNonconformityReasonsPage: reasons failed', error);
      setReasonItems([]);
      setReasonError('Uygunsuzluk nedenleri yüklenirken bir hata oluştu.');
    } finally {
      setReasonLoading(false);
    }
  }, [selectedAttributeId]);

  React.useEffect(() => {
    if (reasonDrawerVisible) {
      void loadReasons();
    }
  }, [loadReasons, reasonDrawerVisible]);

  const openReasonDrawer = (attribute: InspectorAttribute) => {
    setSelectedAttribute(attribute);
    setActiveTab('PRIVATE');
    setReasonSearchText('');
    setReasonDrawerVisible(true);
  };

  const closeReasonDrawer = () => {
    setReasonDrawerVisible(false);
    setActiveTab('PRIVATE');
    setReasonSearchText('');
    setSelectedAttribute(null);
    setReasonItems([]);
    setReasonError(null);
  };

  const openCreateDrawer = () => {
    if (!selectedAttributeId) {
      message.error('Parametre bilgisi bulunamadı.');
      return;
    }
    if (!currentAccountId) {
      message.error('Kullanıcı bilgisi bulunamadı.');
      return;
    }
    setEditingReason(null);
    form.setFieldsValue({
      attributeId: selectedAttributeId,
      accountId: currentAccountId,
      isGeneral: false,
      abbreviation: '',
      description: '',
    });
    setCreateDrawerVisible(true);
  };

  const openEditDrawer = (reason: INonconformityReason) => {
    const ownerAccountId = Number(reason?.accountId ?? reason?.account?.id);
    if (reason?.isGeneral === true || ownerAccountId !== currentAccountId) {
      return;
    }
    setEditingReason(reason);
    form.setFieldsValue({
      attributeId: reason.attributeId ?? selectedAttributeId,
      accountId: ownerAccountId,
      isGeneral: false,
      abbreviation: reason.abbreviation ?? '',
      description: reason.description ?? '',
    });
    setCreateDrawerVisible(true);
  };

  const closeCreateDrawer = () => {
    if (createSubmitting) {
      return;
    }
    setCreateDrawerVisible(false);
    setEditingReason(null);
    form.resetFields();
  };

  const handleSubmitReason = async (values: any) => {
    if (!selectedAttributeId) {
      message.error('Parametre bilgisi bulunamadı.');
      return;
    }
    if (!currentAccountId) {
      message.error('Kullanıcı bilgisi bulunamadı.');
      return;
    }
    const payload: INonconformityReasonPayload = {
      abbreviation: ensureStringValue(values?.abbreviation).trim(),
      description: ensureStringValue(values?.description).trim(),
      isGeneral: false,
      attributeId: selectedAttributeId,
      accountId: currentAccountId,
    };
    if (!payload.abbreviation) {
      message.error('Kısaltma zorunludur.');
      return;
    }
    if (!payload.description) {
      message.error('Açıklama zorunludur.');
      return;
    }

    setCreateSubmitting(true);
    try {
      if (editingReason) {
        await NonconformityReasonService.update(editingReason.id, payload);
        message.success('Uygunsuzluk nedeni güncellendi.');
      } else {
        await NonconformityReasonService.create(payload);
        message.success('Uygunsuzluk nedeni oluşturuldu.');
      }
      setCreateDrawerVisible(false);
      setEditingReason(null);
      form.resetFields();
      await loadReasons();
    } catch (error: any) {
      console.error('InspectorNonconformityReasonsPage: save failed', error);
      const errorMessage =
        ensureStringValue(error?.response?.data?.message).trim() ||
        'Uygunsuzluk nedeni kaydedilirken bir hata oluştu.';
      message.error(errorMessage);
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleDeleteReason = async (reason: INonconformityReason) => {
    try {
      await NonconformityReasonService.remove(reason.id);
      message.success('Uygunsuzluk nedeni silindi.');
      await loadReasons();
    } catch (error: any) {
      console.error('InspectorNonconformityReasonsPage: delete failed', error);
      const errorMessage =
        ensureStringValue(error?.response?.data?.message).trim() ||
        'Uygunsuzluk nedeni silinirken bir hata oluştu.';
      message.error(errorMessage);
    }
  };

  const columns = [
    {
      title: 'Parametre',
      dataIndex: 'name',
      render: (value: string, record: InspectorAttribute) => (
        <Space direction='vertical' size={0}>
          <Text strong={record.isGroup}>{value || `Parametre #${record.id}`}</Text>
        </Space>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 180,
      align: 'right' as const,
      render: (_: unknown, record: InspectorAttribute) =>
        record.isGroup ? null : (
          <Button
            size='small'
            onClick={(event) => {
              event.stopPropagation();
              openReasonDrawer(record);
            }}
          >
            Uygunsuzluk Nedenleri
          </Button>
        ),
    },
  ];

  return (
    <Card
      className='br-30px kontrolx-card'
      title={(
        <Row align='bottom'>
          <Typography.Title level={4} style={{ margin: 0 }}>Uygunsuzluk Nedenleri</Typography.Title>
          <Typography.Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
            <strong>({attributes.length})</strong> parametre listelendi
          </Typography.Text>
        </Row>
      )}
      extra={(
        <Input.Search
          allowClear
          placeholder='Parametre ara'
          prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
          className='d-input d-input-form d-input-filter'
          style={{ width: 260 }}
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value || '');
          }}
          onSearch={(value) => {
            setSearchInput((value || '').trim());
          }}
        />
      )}
    >
      <Table
        rowKey='key'
        dataSource={treeData}
        columns={columns as any}
        loading={attributesLoading}
        onRow={(record) => ({
          onClick: () => {
            if (!record.isGroup) {
              openReasonDrawer(record);
            }
          },
          style: { cursor: record.isGroup ? 'default' : 'pointer' },
        })}
        pagination={false}
        expandable={{
          expandRowByClick: true,
          rowExpandable: (record) => record.isGroup === true,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            if (!record.key) {
              return;
            }
            setExpandedRowKeys((prev) =>
              expanded ? [...prev, record.key as string] : prev.filter((key) => key !== record.key),
            );
          },
        }}
      />

      <Drawer
        title='Uygunsuzluk Nedenleri'
        open={reasonDrawerVisible}
        extra={(
          <Button
            type='primary'
            onClick={openCreateDrawer}
            disabled={!selectedAttributeId || !currentAccountId}
          >
            Yeni Uygunsuzluk Nedeni Ekle
          </Button>
        )}
        width={520}
        onClose={closeReasonDrawer}
      >
        <Space direction='vertical' size={12} style={{ width: '100%' }}>
          {selectedAttribute ? (
            <Text type='secondary'>Parametre: {selectedAttribute.name || `Parametre #${selectedAttribute.id}`}</Text>
          ) : null}
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as 'PRIVATE' | 'ALL')}
            items={[
              { key: 'PRIVATE', label: `Özel (${privateReasons.length})` },
              { key: 'ALL', label: `Tümü (${reasonItems.length})` },
            ]}
          />
          <Input
            allowClear
            value={reasonSearchText}
            onChange={(event) => setReasonSearchText(event.target.value)}
            placeholder='Kısaltma veya açıklamada ara'
          />
          {reasonLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : reasonError ? (
            <Alert type='error' showIcon message={reasonError} />
          ) : searchedReasons.length ? (
            <Space direction='vertical' size={10} style={{ width: '100%' }}>
              {searchedReasons.map((reason) => {
                const ownerName = `${reason.account?.firstName ?? ''} ${reason.account?.lastName ?? ''}`.trim();
                const ownerText = ownerName || reason.account?.email || null;
                const ownerAccountId = Number(reason.accountId ?? reason.account?.id);
                const isOwnPrivateReason =
                  reason.isGeneral !== true && Number.isFinite(ownerAccountId) && ownerAccountId === currentAccountId;
                const canEditReason = isOwnPrivateReason;
                const canDeleteReason = isOwnPrivateReason;
                const editTooltip = canEditReason
                  ? 'Düzenle'
                  : reason.isGeneral === true
                      ? 'Genel uygunsuzluk nedenleri bu ekrandan düzenlenemez.'
                      : 'Yalnızca kendi eklediğiniz uygunsuzluk nedenlerini düzenleyebilirsiniz.';
                const deleteTooltip = canDeleteReason
                  ? 'Sil'
                  : reason.isGeneral === true
                      ? 'Genel uygunsuzluk nedenleri bu ekrandan silinemez.'
                      : 'Yalnızca kendi eklediğiniz uygunsuzluk nedenlerini silebilirsiniz.';
                return (
                  <Card
                    key={reason.id}
                    size='small'
                    title={reason.abbreviation || `Uygunsuzluk Nedeni #${reason.id}`}
                    extra={(
                      <Space size={6}>
                        <Tooltip title={editTooltip}>
                          <span>
                            <Button
                              size='small'
                              icon={<EditOutlined />}
                              disabled={!canEditReason}
                              onClick={() => openEditDrawer(reason)}
                            />
                          </span>
                        </Tooltip>
                        <Popconfirm
                          title='Bu uygunsuzluk nedenini silmek istediğinize emin misiniz?'
                          onConfirm={() => handleDeleteReason(reason)}
                          okText='Evet'
                          cancelText='Hayır'
                          disabled={!canDeleteReason}
                        >
                          <Tooltip title={deleteTooltip}>
                            <span>
                              <Button
                                danger
                                size='small'
                                icon={<DeleteOutlined />}
                                disabled={!canDeleteReason}
                              />
                            </span>
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    )}
                  >
                    <Space direction='vertical' size={6} style={{ width: '100%' }}>
                      <Text>{reason.description || '-'}</Text>
                      {ownerText ? <Text type='secondary'>{ownerText}</Text> : null}
                    </Space>
                  </Card>
                );
              })}
            </Space>
          ) : (
            <Alert
              type='info'
              showIcon
              message={reasonSearchText.trim().length ? 'Arama kriterine uygun uygunsuzluk nedeni bulunamadı.' : 'Bu sekme için tanımlı uygunsuzluk nedeni bulunamadı.'}
            />
          )}
        </Space>
      </Drawer>

      <Drawer
        title={editingReason ? 'Uygunsuzluk Nedeni Düzenle' : 'Yeni Uygunsuzluk Nedeni'}
        open={createDrawerVisible}
        width={520}
        destroyOnClose
        onClose={closeCreateDrawer}
      >
        <Form
          layout='vertical'
          form={form}
          onFinish={handleSubmitReason}
          initialValues={{
            isGeneral: false,
            attributeId: selectedAttributeId,
            accountId: currentAccountId,
          }}
        >
          <Form.Item
            name='attributeId'
            label='Parametre'
            rules={[{ required: true, message: 'Parametre seçiniz.' }]}
          >
            <Select disabled>
              {selectedAttributeId ? (
                <Option value={selectedAttributeId}>
                  {selectedAttribute?.name || `Parametre #${selectedAttributeId}`}
                </Option>
              ) : null}
            </Select>
          </Form.Item>
          <Form.Item
            name='accountId'
            label='Kullanıcı'
            rules={[{ required: true, message: 'Kullanıcı seçiniz.' }]}
          >
            <Select disabled>
              {currentAccountId ? <Option value={currentAccountId}>{currentAccountLabel}</Option> : null}
            </Select>
          </Form.Item>
          <Form.Item
            name='isGeneral'
            label='Kapsam'
            rules={[{ required: true, message: 'Kapsam seçiniz.' }]}
          >
            <Select disabled>
              <Option value={false}>Özel</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name='abbreviation'
            label='Kısaltma'
            rules={[{ required: true, message: 'Kısaltma zorunludur.' }]}
          >
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item
            name='description'
            label='Açıklama'
            rules={[{ required: true, message: 'Açıklama zorunludur.' }]}
          >
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button block type='primary' htmlType='submit' loading={createSubmitting}>
              {editingReason ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
};

export default InspectorNonconformityReasonsPage;
